import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  TextField,
  Container,
  Typography,
  Paper,
  Box,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  MenuItem
} from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';
import { useDropzone } from "react-dropzone";
import axios from 'axios';
import Swal from "sweetalert2";
import * as Yup from 'yup';
import { apiRegistry, isEndpointError } from '../lib/api-registry';

// Define validation schema outside the component
const validationSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  studentId: Yup.string().required("Student ID is required"),
  batch: Yup.string().matches(/^\d+$/, "Batch must be a number").required("Batch is required"),
  email: Yup.string().email("Invalid email format").required("Student Email is required"),
  declarations: Yup.object({
    ownWork: Yup.boolean().oneOf([true], "You must confirm this"),
    plagiarismPolicy: Yup.boolean().oneOf([true], "You must confirm this"),
    referenced: Yup.boolean().oneOf([true], "You must confirm this"),
  }),
  files: Yup.array().min(1, "At least one file must be uploaded"),
  signature: Yup.string().required("Signature is required"),
  lecturer: Yup.string().required("Lecturer is required"),
});

// Field definitions for reuse
const personalFields = [
  { label: "Student ID Number", name: "studentId" },
  { label: "Unit Name", name: "unitName" },
  { label: "Batch", name: "batch" },
  { label: "Email", name: "email", type: "email" },
];

// Add lecturer options
const lecturerOptions = [
  { value: "", label: "" },
  { value: "Dimi", label: "Dimi Withanage" },
  { value: "Prince", label: "Prince Prinja" },
  { value: "Deepa", label: "Deepa Karki" },
  { value: "Navdeep", label: "Navdeep Singh" },
  { value: "Yunita", label: "Yunita Panther" },
  { value: "Narpat", label: "Narpat Shekhawat" },
];

const declarationItems = [
  { name: "ownWork", text: "I declare that this task is all my own work and I have not cheated or plagiarized." },
  { name: "plagiarismPolicy", text: "I understand that if I am found to have plagiarized, cheated, or colluded, action will be taken against me." },
  { name: "referenced", text: "I have correctly referenced all resource and reference texts throughout these assessment tasks." },
];

const CustomerForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    studentId: "",
    unitName: "",
    lecturer: "",
    batch: "",
    email: "",
    declarations: { ownWork: false, plagiarismPolicy: false, referenced: false },
    files: [],
    signature: "",
  });
  const [loading, setLoading] = useState(false);
  const signatureRef = useRef(null);

  // Initialize API registry and ping to prevent cold starts
  useEffect(() => {
    apiRegistry.initialize().then(() => {
      const pingUrl = apiRegistry.currentBaseUrl;
      axios.get(`${pingUrl}/ping`).catch((err) => {
        if (!err.response || isEndpointError(err.response.status)) {
          apiRegistry.markEndpointFailed(pingUrl);
        }
      });
    });
  }, []);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [type === "checkbox" ? "declarations" : name]: type === "checkbox"
        ? { ...prevData.declarations, [name]: checked }
        : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const baseUrl = apiRegistry.currentBaseUrl;

    try {
      // Create FormData for multipart file upload
      const formDataObj = new FormData();

      // Add basic fields
      formDataObj.append("FirstName", formData.firstName);
      formDataObj.append("LastName", formData.lastName);
      formDataObj.append("StudentId", formData.studentId);
      formDataObj.append("UnitName", formData.unitName);
      formDataObj.append("Lecturer", formData.lecturer);
      formDataObj.append("Batch", formData.batch);
      formDataObj.append("Email", formData.email);

      // Add signature
      const signatureDataURL = signatureRef.current.toDataURL("image/png");
      formDataObj.append("Signature", signatureDataURL);

      // Add declarations
      formDataObj.append("OwnWork", formData.declarations.ownWork);
      formDataObj.append("PlagiarismPolicy", formData.declarations.plagiarismPolicy);
      formDataObj.append("Referenced", formData.declarations.referenced);

      // Append all files
      formData.files.forEach((file) => {
        formDataObj.append("Files", file);
      });

      // Submit form data
      await axios.post(`${baseUrl}/v1/asbt/submission`, formDataObj, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire({
        title: "Assessment Submitted",
        text: "Your assessment was submitted successfully!",
        icon: "success"
      });
    } catch (error) {
      // Mark endpoint as failed on 502/503/504 or connection errors
      if (!error.response || isEndpointError(error.response.status)) {
        apiRegistry.markEndpointFailed(baseUrl);
      }
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error.response?.data || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    signatureRef.current.clear();
    setFormData((prevData) => ({ ...prevData, signature: "" }));
  };

  const onDrop = (acceptedFiles) => {
    setFormData((prevData) => ({
      ...prevData,
      files: [...prevData.files, ...acceptedFiles],
    }));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "application/pdf, image/*",
  });

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 5, borderRadius: 2, width: '100%', mx: 'auto' }}>
        {/* Header Section */}
        <Box textAlign="center" mb={4}>
          <img src="/images/asbt_logo.png" alt="Logo" style={{ width: "260px", height: "auto" }} />
        </Box>
        <Typography variant="h4" align="left" gutterBottom sx={{ color: "black", fontWeight: 'bold' }}>
          Assessment Task
        </Typography>

        <Divider sx={{ borderBottomWidth: 2, backgroundColor: "rgba(0, 0, 0, 0.28)", mb: 4 }} />

        <form onSubmit={handleSubmit}>
          {/* Name Fields */}
          <Box display="flex" gap={4} mb={6} sx={{ mt: 8 }}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              sx={{ '& .MuiInputBase-root': { height: 56 } }}
              value={formData.firstName}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </Box>

          {/* Personal Information Fields */}
          {personalFields.slice(0, 2).map((field) => (
            <TextField
              key={field.name}
              fullWidth
              label={field.label}
              name={field.name}
              type={field.type || "text"}
              value={formData[field.name]}
              onChange={handleChange}
              required
              sx={{ mb: 6 }}
            />
          ))}

          <TextField
            fullWidth
            label="Batch"
            name="batch"
            type="text"
            value={formData.batch}
            onChange={handleChange}
            required
            sx={{ mb: 6 }}
          />

          <TextField
            select
            fullWidth
            label="Lecturer"
            name="lecturer"
            value={formData.lecturer}
            onChange={handleChange}
            required
            sx={{ mb: 6 }}
          >
            {lecturerOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Student Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            sx={{ mb: 6 }}
          />

          {/* Declaration Section */}
          <Typography variant="h6" align="left" mt={4}>
            Student Declaration
          </Typography>
          <Divider sx={{ borderBottomWidth: 2, backgroundColor: "rgba(0, 0, 0, 0.28)", mb: 4 }} />

          <Typography variant="h6" align="left" sx={{ fontWeight: 'bold', mt: 2, mb: 2, fontSize: '1.0rem' }}>
            To be filled out and submitted with assessment responses
            <span style={{ color: 'red' }}>*</span>
          </Typography>

          {/* Declaration Checkboxes */}
          {declarationItems.map((item) => (
            <FormControlLabel
              key={item.name}
              control={
                <Checkbox
                  name={item.name}
                  checked={formData.declarations[item.name]}
                  onChange={handleChange}
                />
              }
              label={item.text}
            />
          ))}

          {/* File Upload Section */}
          <Box mt={8} textAlign="left">
            <Typography variant="h6" sx={{ mb: 1 }}>Upload Your Assessment</Typography>
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #000",
                borderRadius: "5px",
                p: 8,
                cursor: "pointer"
              }}
            >
              <input {...getInputProps()} />
              <Typography>Drag and drop files here, or click to select files</Typography>
            </Box>

            {formData.files.length > 0 && (
              <Box mt={2}>
                <Typography variant="body1">Uploaded Files:</Typography>
                <ul>
                  {formData.files.map((file, index) => (
                    <li key={index}>{file.name}</li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>

          {/* Signature Pad */}
          <Box mt={8} textAlign="left">
            <Typography variant="h6">Signature here</Typography>
            <Box sx={{ border: "2px solid #000", borderRadius: "5px", height: "200px", mt: 1 }}>
              <SignatureCanvas
                ref={signatureRef}
                penColor="black"
                canvasProps={{ width: "550%", height: "200%" }}
              />
            </Box>
            <Button
              variant="contained"
              color="error"
              onClick={handleClear}
              sx={{
                mt: 1,
                width: "25%",
                padding: "8px 8px",
                height: "35px",
                fontSize: "0.75rem",
                backgroundColor: "gray",
              }}
            >
              Clear Signature
            </Button>
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            sx={{
              mt: 3,
              width: "100%",
              py: 1,
              backgroundColor: "black",
              color: "white"
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Submit Form"}
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default CustomerForm;
